import axios from "axios";
import API_BASE_URL from "./config";

const API = axios.create({
  baseURL: API_BASE_URL.endsWith('/api') 
    ? API_BASE_URL 
    : `${API_BASE_URL}/api`,
  withCredentials: true,
});

API.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If it's a 401 and we haven't retried yet, and it's not the refresh endpoint itself
    if (error.response?.status === 401 && !originalRequest._retry && originalRequest.url !== '/auth/refresh') {
      if (isRefreshing) {
        return new Promise(function(resolve, reject) {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = 'Bearer ' + token;
          return API(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Backend expects the refresh_token cookie
        await axios.post(`${API.defaults.baseURL}/auth/refresh`, {}, { withCredentials: true });
        
        // The backend /auth/refresh endpoint only updates the cookie.
        // It does not return a new JWT string for localStorage.
        // If the localStorage token is expired, we will still be sending it via the request interceptor.
        // However, if the session is alive via cookies, maybe we don't need the Authorization header?
        // Actually, let's let the request go through again. If it fails again, it will reject.
        // Wait, if the server checks the cookie, it might work. Let's resolve the queue.
        processQueue(null, localStorage.getItem("token"));
        return API(originalRequest);
      } catch (err) {
        processQueue(err, null);
        // Refresh failed, clear localStorage and redirect to login
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        window.location.href = "/login";
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default API;